<template>
  <div class="min-h-screen flex flex-col bg-gray-50 items-center justify-start relative overflow-hidden">
    <!-- Loader -->
    <div
      v-if="isLoading"
      class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
    </div>

    <!-- Header -->
    <div class="w-full max-w-6xl flex justify-between items-start mb-10">
      <div>
        <h1 class="text-3xl md:text-4xl font-bold text-gray-900 mb-1">Membership Plans</h1>
        <p class="text-gray-500 text-sm md:text-base">
          Choose the plan that best fits your analytics needs.
        </p>
      </div>

      <button
      @click="showVideoModal = true"
        class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium shadow-sm hover:bg-blue-700 transition"
      >
        <i class="ri-question-line text-lg"></i> Help
      </button>
    </div>

    <!-- Message -->
    <div
      v-if="msgTxt"
      :class="`p-3 mb-6 rounded-lg text-sm ${
        msgType === 'success'
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700'
      }`"
    >
      {{ msgTxt }}
    </div>

    <!-- Plans -->
    <div
      class="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-6xl items-start justify-center flex-grow"
    >
      <div
        v-for="(plan, index) in plans"
        :key="index"
        :class="[
          'rounded-2xl shadow-sm p-8 flex flex-col items-center transition-all duration-300 relative',
          index === 1
            ? 'bg-gradient-to-b from-blue-50 to-white border-2 border-blue-500 scale-105 shadow-lg'
            : 'bg-white border border-gray-200 hover:shadow-md hover:scale-[1.02]'
        ]"
      >
        <!-- Recommended Tag -->
        <div
          v-if="index === 1"
          class="absolute -top-4 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm"
        >
          Recommended
        </div>

        <h2 class="text-xl font-semibold text-gray-800 mb-2">{{ plan.plan_title }}</h2>
        <p class="text-4xl font-bold text-gray-900 mb-1">
          ${{ plan.plan_price }}
          <span class="text-base font-normal">/{{ plan.plan_type.toLowerCase() }}</span>
        </p>
        <p class="text-gray-500 text-sm mb-6">{{ plan.plan_desc }}</p>

        <!-- Features -->
        <ul class="space-y-3 text-gray-600 text-sm mb-8 text-left w-full">
          <li
            v-for="(feature, fIndex) in parseFeatures(plan.plan_features)"
            :key="fIndex"
            class="flex items-center gap-2"
          >
            <i class="ri-check-line text-blue-600"></i>
            {{ feature.title }}
          </li>
        </ul>

        <button
        v-if="plan.my_plan==0"
          @click="openPayment(plan.plan_id,plan.plan_title, plan.plan_price)"
          class="bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-medium px-6 py-2 rounded-lg hover:shadow-md transition"
        >
          Subscribe
        </button>
         <button
          v-if="plan.my_plan == 1"
          :disabled="true" 
          class="bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-medium px-6 py-2 rounded-lg hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Subscribed
        </button>

      </div>
    </div>

    <!-- Payment Modal -->
    <transition name="slide-fade">
      <div
        v-if="showModal"
        class="fixed top-16 left-1/2 transform -translate-x-1/2 bg-white shadow-2xl rounded-2xl border border-gray-100 p-8 w-[90%] max-w-md z-50 text-center animate-fadeIn"
      >
        <h3 class="text-xl font-semibold text-gray-800 mb-2">{{ selectedPlan }} Plan</h3>
        <p class="text-3xl font-bold text-gray-900 mb-4">${{ selectedPrice }} /month</p>
        <p class="text-gray-500 text-sm mb-6">Choose a payment method to proceed.</p>

        <!-- Payment Options -->
        <div class="flex flex-col gap-4 mb-8">
          
          <button
            @click="goforpayment('PayPal')"
            class="flex items-center justify-center border border-gray-200 rounded-xl py-3 hover:border-blue-400 hover:bg-blue-50 transition w-full"
          >
            <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" class="h-8" />
          </button>

          <button
          @click="goforpayment('Razorpay')"
            class="flex items-center justify-center border border-gray-200 rounded-xl py-3 hover:border-blue-400 hover:bg-blue-50 transition w-full"
          >
            <img src="https://upload.wikimedia.org/wikipedia/commons/8/89/Razorpay_logo.svg" alt="Razorpay" class="h-8" />
          </button>

          <button
          @click="goforpayment('Stripe')"
            class="flex items-center justify-center border border-gray-200 rounded-xl py-3 hover:border-blue-400 hover:bg-blue-50 transition w-full"
          >
            <img src="https://cdn.worldvectorlogo.com/logos/stripe-4.svg" alt="Stripe" class="h-8" />
          </button>
        </div>

        <div class="flex justify-center gap-3">
          <button
            @click="showModal = false"
            class="px-5 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <!-- <button
            @click="confirmPayment"
            class="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Confirm
          </button> -->
        </div>
      </div>
    </transition>

    <!-- Video Help Modal -->
<transition name="fade">
  <div
    v-if="showVideoModal"
    class="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50"
    @click.self="showVideoModal = false"
  >
    <div
      class="bg-white rounded-2xl shadow-2xl w-[90%] max-w-3xl relative overflow-hidden"
      style="animation: fadeInScale 0.3s ease-out;"
    >
      <!-- Close Button -->
      <button
  @click="showVideoModal = false"
  class="absolute top-3 right-3 rounded-full transition flex items-center justify-center"
  style="
    z-index: 10;
    width: 38px;
    height: 38px;
    background-color: #2563eb; /* blue-600 */
    color: #ffffff;
    border: none;
    cursor: pointer;
    box-shadow: 0 3px 8px rgba(37, 99, 235, 0.4);
    transition: all 0.2s ease;
  "
  onmouseover="this.style.backgroundColor='#1d4ed8'"
  onmouseout="this.style.backgroundColor='#2563eb'"
>
  <i class="ri-close-line" style="font-size: 1.25rem; color: #fff;"></i>
</button>


      <!-- Video Player -->
      <div class="w-full h-0 pb-[56.25%] relative bg-black">
        <iframe
          :src="videoUrl"
          allow="autoplay; encrypted-media"
          allowfullscreen
          frameborder="0"
          class="absolute top-0 left-0 w-full h-full rounded-2xl"
        ></iframe>
      </div>
    </div>
  </div>
</transition>


    <!-- Overlay -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
      @click="showModal = false"
    ></div>
  </div>
</template>

<script>
import { getPlanList } from "@/apis/memplanapis";
import { apiBaseUrl } from "@/apis/appconst";
import { purPlan } from "@/apis/webapis";

export default {
  name: "PricingPage",
  data() {
    return {
      showModal: false,
      showVideoModal: false,
      selectedPlan: "",
      selectedPlanId: "",
      selectedPrice: 0,
      plans: [],
      msgTxt: "",
      msgType: "",
      isLoading: false,
      ldata: null,
    };
  },
  methods: {
    async purPlanNow($plan_id){
       try {
        const res = await purPlan(
          $plan_id,
          this.ldata.user_id
        );
        if (res.status && res.user_dt) {
          window.location.reload();
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    goforpayment($ptype){
      var payUrl = "razorpay/redirectToPaymentGateway";
      if(($ptype+"").localeCompare("PayPal")==0){
        payUrl = "paypal/redirectToPaymentGateway";
      }
      else if(($ptype+"").localeCompare("Stripe")==0){
        payUrl = "stripe/redirectToPaymentGateway";
      }

      payUrl = apiBaseUrl+payUrl+"?rtype=mem&amount="+this.selectedPlanId+"&curr=USD&uid="+this.ldata.user_id;

      window.location.href = payUrl;
    },
    parseFeatures(features) {
      try {
        return JSON.parse(features);
      } catch {
        return [];
      }
    },
    async fetchPlans() {
      try {
        this.isLoading = true;
        const res = await getPlanList(0, 10, this.ldata.user_id || 0);
        if (res.status) {
          // Sort plans by price for consistent layout (optional)
          this.plans = (res.records || []).sort((a, b) => a.plan_price - b.plan_price);
        } else {
          this.msgType = "danger";
          this.msgTxt = res.message || "Failed to load plans.";
        }
      } catch {
        this.msgType = "danger";
        this.msgTxt = "Error fetching plans.";
      } finally {
        this.isLoading = false;
      }
    },
    openPayment(pid,plan, price) {
      if(parseFloat(price+"")<=0){
        this.purPlanNow(pid);
        return;
      }
      this.selectedPlan = plan;
      this.selectedPlanId = pid;
      this.selectedPrice = price;
      this.showModal = true;
    },
    confirmPayment() {
      this.showModal = false;
      this.msgType = "success";
      this.msgTxt = "Redirecting to payment gateway...";
    },
  },
  mounted(){
    if ((window.location.href + "").includes("udata")) {
      const udata = this.$route.query.udata;
      if(udata!=null && udata!=undefined && udata.length>0){
        const ldata = JSON.parse(atob(udata));
        if(ldata!=null && ldata!=undefined){
          this.msgType = ldata.type;
          this.msgTxt = ldata.msg;
        }
      }
    }
  },
  created() {
    const ldataRaw = sessionStorage.getItem("ldata");
    this.ldata = ldataRaw ? JSON.parse(ldataRaw) : { user_id: 0 };
    this.fetchPlans();
  },
};
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.35s ease;
}
.slide-fade-enter-from {
  opacity: 0;
  transform: translate(-50%, -30%);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -20%);
}
.animate-fadeIn {
  animation: fadeInUp 0.4s ease-out both;
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate(-50%, -40%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fadeInScale {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

</style>
